import { useEffect, useState } from "react";
import { apiFetch } from "./api";

const TYPE_OPTIONS = [
  { value: "erg", label: "Erg" },
  { value: "water", label: "On the water" },
  { value: "run", label: "Run" },
  { value: "lift", label: "Lift" },
  { value: "other", label: "Other" },
];

export default function OyoWorkoutModal({ workoutId, onClose }) {
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!workoutId) {
      setWorkout(null);
      setLoading(false);
      return;
    }

    const loadWorkout = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await apiFetch(`/oyo-workouts/${workoutId}`);
        setWorkout(data);
      } catch (e) {
        console.error("Failed to load OYO workout", e);
        setError(e?.message || "Failed to load workout");
        setWorkout(null);
      } finally {
        setLoading(false);
      }
    };

    loadWorkout();
  }, [workoutId]);

  if (!workoutId) return null;

  const getTypeLabel = (value) => {
    const option = TYPE_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(`${dateStr}T00:00:00`);
      const options = { weekday: "short", month: "short", day: "numeric", year: "numeric" };
      return d.toLocaleDateString("en-US", options);
    } catch {
      return dateStr;
    }
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = Date.now();
      const diffMs = now - date.getTime();
      const diffMinutes = Math.round(diffMs / 60000);
      if (diffMinutes < 60) {
        return `${diffMinutes || 1} min ago`;
      }
      const diffHours = Math.round(diffMinutes / 60);
      if (diffHours < 24) {
        return `${diffHours} hr${diffHours === 1 ? "" : "s"} ago`;
      }
      const diffDays = Math.round(diffHours / 24);
      return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
    } catch {
      return "";
    }
  };

  return (
    <div
      className="modalOverlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="modalCard"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 600,
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-elevated)",
          color: "var(--text)",
          borderRadius: 8,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.35)",
        }}
      >
        <div className="modalCardContent" style={{ padding: 24 }}>
          <div className="modalHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 className="h2" style={{ margin: 0 }}>
              OYO Workout Details
            </h3>
            <button
              className="modal-close"
              aria-label="Close"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: 28,
                cursor: "pointer",
                lineHeight: 1,
                color: "var(--muted)",
              }}
            >
              &times;
            </button>
          </div>

          {loading && (
            <p className="help" style={{ textAlign: "center", padding: 20 }}>Loading workout...</p>
          )}

          {error && (
            <p className="help" style={{ color: "red", textAlign: "center", padding: 20 }}>{error}</p>
          )}

          {!loading && !error && workout && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Athlete Name (for coaches) */}
              {workout.athleteName && (
                <div>
                  <label className="label" style={{ display: "block", marginBottom: 4 }}>Athlete</label>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{workout.athleteName}</div>
                </div>
              )}

              {/* Date */}
              <div>
                <label className="label" style={{ display: "block", marginBottom: 4 }}>Date</label>
                <div style={{ fontSize: 16 }}>{formatDate(workout.performedOn)}</div>
              </div>

              {/* Type */}
              <div>
                <label className="label" style={{ display: "block", marginBottom: 4 }}>Type</label>
                <div style={{ fontSize: 16 }}>{getTypeLabel(workout.workoutType)}</div>
              </div>

              {/* Description */}
              <div>
                <label className="label" style={{ display: "block", marginBottom: 4 }}>What did you do?</label>
                <div
                  style={{
                    fontSize: 16,
                    whiteSpace: "pre-wrap",
                    background: "var(--bg)",
                    padding: 12,
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                  }}
                >
                  {workout.description || "—"}
                </div>
              </div>

              {/* Duration and Distance */}
              <div style={{ display: "flex", gap: 16 }}>
                {workout.durationMinutes != null && workout.durationMinutes !== "" && (
                  <div style={{ flex: 1 }}>
                    <label className="label" style={{ display: "block", marginBottom: 4 }}>Duration</label>
                    <div style={{ fontSize: 16 }}>{workout.durationMinutes} min</div>
                  </div>
                )}
                {workout.distanceMeters != null && workout.distanceMeters !== "" && (
                  <div style={{ flex: 1 }}>
                    <label className="label" style={{ display: "block", marginBottom: 4 }}>Distance</label>
                    <div style={{ fontSize: 16 }}>{Number(workout.distanceMeters).toLocaleString()} m</div>
                  </div>
                )}
              </div>

              {/* Photo */}
              {workout.photoDataUrl && (
                <div>
                  <label className="label" style={{ display: "block", marginBottom: 8 }}>Photo</label>
                  <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
                    <img
                      src={workout.photoDataUrl}
                      alt="Workout"
                      style={{ width: "100%", height: "auto", display: "block" }}
                    />
                  </div>
                </div>
              )}

              {/* Logged timestamp */}
              <div className="help" style={{ marginTop: 8, textAlign: "right" }}>
                Logged {formatRelative(workout.createdAt)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
